import { createFileRoute } from "@tanstack/react-router"
import { fetchDestinations, type DestinationSummary } from "@/lib/destinations"
import { defaultMeta, absoluteUrl, SITE_NAME } from "@/lib/seo"

export const Route = createFileRoute(`/_public/destinations/`)({
  ssr: true,
  // Direct-Postgres server fn (never a self-fetch to /api/trpc). Never throws —
  // degrades to an empty list, which renders the empty state below.
  loader: () => fetchDestinations(),
  head: () =>
    defaultMeta({
      title: `Travel Destinations`,
      description: `Browse city-by-city travel guides and day-by-day itineraries from the ChicTravel community — real trips you can copy and make your own.`,
      path: `/destinations`,
    }),
  component: DestinationsIndexPage,
})

function DestinationsIndexPage() {
  const destinations = Route.useLoaderData()

  // CollectionPage + ItemList so the hub index reads as a structured list of
  // destinations. SSR'd inline (this route is full SSR) so it's in the raw HTML.
  const jsonLd = {
    "@context": `https://schema.org`,
    "@type": `CollectionPage`,
    name: `Travel Destinations`,
    url: absoluteUrl(`/destinations`),
    publisher: { "@type": `Organization`, name: SITE_NAME, url: absoluteUrl(`/`) },
    mainEntity: {
      "@type": `ItemList`,
      itemListElement: destinations.map((d, i) => ({
        "@type": `ListItem`,
        position: i + 1,
        name: d.destination,
        url: absoluteUrl(`/destinations/${d.slug}`),
      })),
    },
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 space-y-10">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="space-y-3 max-w-3xl">
        <h1 className="text-4xl font-extrabold tracking-tight">
          Travel destinations
        </h1>
        <p className="text-muted-foreground text-lg leading-relaxed">
          Every destination below is backed by real, complete trips published by
          ChicTravel travelers — day-by-day itineraries with every stop pinned on
          a live map. Pick a city to see its guides, read them free, and copy any
          one into your own editable plan.
        </p>
      </div>

      {destinations.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-3xl">
          <h2 className="text-base font-bold mb-1">No destinations yet</h2>
          <p className="text-muted-foreground text-sm">
            Check back soon — new guides are published all the time.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {destinations.map((d) => (
            <DestinationCard key={d.slug} destination={d} />
          ))}
        </div>
      )}
    </div>
  )
}

function DestinationCard({ destination }: { destination: DestinationSummary }) {
  const { destination: name, slug, guideCount, image_url } = destination
  return (
    <a href={`/destinations/${slug}`} className="group block">
      <article className="h-full rounded-2xl border border-border bg-card hover:border-primary/40 hover:bg-muted/40 transition-all duration-200 overflow-hidden flex flex-col">
        <div className="relative h-40 overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
          {image_url ? (
            <img
              src={image_url}
              alt={name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 flex items-end p-4">
              <span className="text-white/25 text-3xl font-extrabold tracking-tight leading-none line-clamp-2 select-none">
                {name.split(`,`)[0]}
              </span>
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col gap-1 flex-1">
          <h2 className="text-base font-bold leading-snug group-hover:text-primary transition-colors">
            {name}
          </h2>
          <p className="text-sm text-muted-foreground">
            {guideCount} {guideCount === 1 ? `itinerary` : `itineraries`}
          </p>
        </div>
      </article>
    </a>
  )
}
